import { Dropdown, Input, Menu, message, Modal, PageHeader, Space, Typography } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import CoreProTable, { CoreProTableState } from '../../core/components/CoreProTable';
import { usePageState } from '../../core/hooks/usePageState';
import WalletProvider, { MchWallet } from '../../domain/usdt-providers/walletProvider';
import { AbandonWalletProvider } from '../../domain/usdt-providers/AbandonWalletProvider';
import { WRITE_ABANDON_WALLET } from '../../abandon-wallet/configs/scopes';
import { ProCoreActionType } from '@ant-design/pro-utils';
import { FormInstance } from 'antd/lib/form/hooks/useForm';
import { transfer2LocalTime } from '../../core/utils/timeFormat';
import SystemIdSearchList from '../../domain/components/system-search-list/SystemIdSearchList';
import PaginationTransformer from '../../core/utils/paginationTransformer';
import {
  ChannelValueEnum,
  isRetireSupported,
  RETIRE_UNSUPPORTED_HINT,
  Token,
} from '../../domain/channel/channel';
import { useTranslation } from 'react-i18next';
import ProDescriptions from '@ant-design/pro-descriptions';
import ChangeAccountForm, { ChangeAccountProps, FormCloseEvent } from '../components/ChangeAccountForm';
import { EventProvider } from '../../../event/eventProvider';
import { filter } from 'rxjs/operators';
import { SystemStatusEnum } from '../../domain/system-status';
import * as profitForm from '../components/ProfitWithdrawFrom';
import _ from 'lodash';
import { ADJUST_PAYMENT, CHANGE_WALLET, WRITE_PROFIT_WITHDRAW } from '../configs/scopes';
import { useOperatorLogger } from '../../core/hooks/useOperatorLogger';
import _module from '../configs/module';
import { normalizeOtp } from '../../core/utils/otp';

interface PageOp extends CoreProTableState<MchWallet> {
}

const defaultChangeAccount: ChangeAccountProps = {
  availableBalance: 0,
  txnBalance: 0,
  channel: '',
  token: '',
  systemId: '',
  visible: false,
  walletAddress: '',
  walletType: 'Mch',
};

const defaultProfitProps: profitForm.ProfitWithdrawProps = {
  balance: 0,
  channel: '',
  fromAddress: '',
  systemId: '',
  token: '',
  visible: false,
};

// eslint-disable-next-line
export default (props: { title: string; subTitle: string }) => {
  const actionRef = useRef<ProCoreActionType>();
  const searchFormRef = useRef<FormInstance>();
  const { t } = useTranslation();
  // const [currentRow, setCurrentRow] = useState<MchWallet>();
  // const [restoreFormVisible, setRestoreFormVisible] = useState(false);
  const [changeAccount, setChangeAccount] = useState<ChangeAccountProps>(
    defaultChangeAccount,
  );
  const [profit, setProfit] = useState<profitForm.ProfitWithdrawProps>(
    defaultProfitProps,
  );

  const pageOp = usePageState<PageOp>('PAGE_REMEMBER', {
    params: {},
    collapsed: false,
    pagination: {
      pageSize: 10,
      current: 1,
    },
    needRequest: true,
  });

  const loggger = useOperatorLogger();

  const refresh = () => {
    pageOp.update((v) => {
      v.needRequest = true;
      return v;
    });
    actionRef.current?.reload();
  };

  // 棄用單顆商戶錢包:加入棄用清單後,poke-jobs 不再自動歸集/刷新它的餘額。
  // 沿用既有 abandon-wallets 端點(同時支援商戶/用戶錢包,需 2FA + boSign)。
  const doAbandon = (row: MchWallet) => {
    let code = '';
    Modal.confirm({
      title: '棄用此商戶錢包',
      width: 480,
      centered: true,
      content: (
        <div>
          <p>
            錢包 <strong>{row.walletAddress}</strong> 將加入棄用清單。
          </p>
          <p style={{ color: '#a8071a' }}>
            棄用後系統不再自動歸集、也不再刷新其餘額。若鏈上仍有殘值,需另走一次性回收。
          </p>
          <p style={{ marginBottom: 4 }}>請輸入 2FA 驗證碼:</p>
          <Input
            placeholder="2FA 驗證碼"
            onChange={(e) => (code = e.target.value)}
          />
        </div>
      ),
      okText: '確認棄用',
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: async () => {
        if (!code) {
          message.error('請輸入 2FA 驗證碼');
          throw new Error('no code');
        }
        try {
          const r = await AbandonWalletProvider.abandonWallets({
            addresses: [row.walletAddress],
            code: normalizeOtp(code),
          });
          if (r.notFound?.length) {
            message.warning(
              `這顆不在商戶/用戶錢包裡,未加入:${r.notFound.join(', ')}`,
              8,
            );
          } else {
            message.success(`已棄用 ${r.inserted} 列`);
          }
          loggger.log({
            action: '棄用商戶錢包',
            payload: { walletAddress: row.walletAddress },
            systemId: row.systemId,
          });
          refresh();
        } catch (e: any) {
          // 不再自己跳 message —— adminRequest 攔截器已經對每個失敗的請求跳過一次了,
          // 這裡再跳一次會變成兩個一模一樣的紅框,看起來像失敗了兩次。
          // 仍然往外丟,Modal 才會停在原地讓人重打驗證碼。
          throw e;
        }
      },
    });
  };

  // 停用單顆商戶錢包(available=0)—— 退役前的必經步驟,停用後才准棄用。需 2FA。
  const doDisable = (row: MchWallet) => {
    let code = '';
    Modal.confirm({
      title: '停用此商戶錢包',
      width: 480,
      centered: true,
      content: (
        <div>
          <p>
            錢包 <strong>{row.walletAddress}</strong> 將被停用(available=0)。
          </p>
          <p style={{ color: '#a8071a' }}>
            停用後系統不再派它收新入金。恢復需走 SQL 或撤回棄用。
          </p>
          <p style={{ marginBottom: 4 }}>請輸入 2FA 驗證碼:</p>
          <Input
            placeholder="2FA 驗證碼"
            onChange={(e) => (code = e.target.value)}
          />
        </div>
      ),
      okText: '確認停用',
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: async () => {
        if (!code) {
          message.error('請輸入 2FA 驗證碼');
          throw new Error('no code');
        }
        try {
          const r = await AbandonWalletProvider.disableWallets({
            addresses: [row.walletAddress],
            code: normalizeOtp(code),
          });
          if (r.notFound?.length) {
            message.warning(`未找到:${r.notFound.join(', ')}`, 8);
          } else {
            message.success(`已停用 ${r.disabled} 列`);
          }
          refresh();
        } catch (e: any) {
          // 同上:攔截器已經跳過訊息了,這裡只負責讓 Modal 留著。
          throw e;
        }
      },
    });
  };

  const replaceWallet = async (param: {
    systemId: string;
    channel: string;
  }) => {
    Modal.confirm({
      title: '更換商戶錢包',
      content: '請確認是否要更換商戶錢包',
      onOk: async () => {
        await WalletProvider.replaceWallet(param)
          .then((res) => {
            loggger.log({
              action: '更換商戶錢包',
              payload: { req: param, res: res.data },
              systemId: param.systemId,
            });
            Modal.success({
              title: t('common.success'),
              content: (
                <ProDescriptions column={1}>
                  <ProDescriptions.Item label={t('payment.wallet_address')}>
                    {res.data.walletAddress}
                  </ProDescriptions.Item>
                </ProDescriptions>
              ),
            });
            refresh();
          })
          .catch((e) => {
            loggger.log({
              action: '更換商戶錢包',
              payload: { req: param, res: e },
              systemId: param.systemId,
            });
          });
      },
    });
  };

  useEffect(() => {
    searchFormRef.current?.setFieldsValue(pageOp.get().params);
  }, [searchFormRef, pageOp]);

  useEffect(() => {
    EventProvider.subject
      .pipe(filter((x) => x.eventSymbol === FormCloseEvent.symbol))
      .subscribe(() => {
        setChangeAccount(defaultChangeAccount);
      });
    EventProvider.subject
      .pipe(
        filter(
          (x) =>
            x.eventSymbol === FormCloseEvent.symbol ||
            x.eventSymbol === profitForm.FormClosedEvent.symbol,
        ),
      )
      .subscribe(() => {
        refresh();
      });
    EventProvider.subject
      .pipe(filter((x) => x.eventSymbol === profitForm.FormClosedEvent.symbol))
      .subscribe(() => {
        setProfit(defaultProfitProps);
      });
    // eslint-disable-next-line
  }, []);
  return (
    <>
      <PageHeader title={props.title} subTitle={''} extra={<></>} />
      <CoreProTable<MchWallet>
        bordered
        rowKey={(row) =>
          `${row.systemId}:${row.channel}:${row.token}:${row.walletAddress}`
        }
        updateTableState={(state) =>
          pageOp.update((v: any) => ({ ...v, ...state }))
        }
        scroll={{ x: 'fixed' }}
        tableState={pageOp.get()}
        // requestData={async (params) => {
        //   const res = await WalletProvider.getMchWalletPage(
        //     PaginationTransformer.paramTransfer(params),
        //   );
        //   return PaginationTransformer.responseTransfer(res);
        // }}

        requestData={(params) =>
          WalletProvider.getMchWalletPage(
            PaginationTransformer.paramTransfer({ ...params, isAdmin: true }),
          ).then((rs) => {
            loggger.log({
              action: '查詢',
              payload: { req: params, res: rs },
              systemId: params.systemId,
            });
            return PaginationTransformer.responseTransfer(rs);
          })
        }
        actionRef={actionRef as any}
        formRef={searchFormRef}
        columns={[
          {
            fixed: 'left',
            hideInSearch: false,
            width: 100,
            align: 'center',
            title: '商戶名稱',
            dataIndex: 'systemId',
            renderFormItem: (_: any, row, form: FormInstance<any>) => (
              <SystemIdSearchList
                onChange={(value) =>
                  form.setFieldsValue({ [_.dataIndex as string]: value })
                }
                systemId={pageOp.get().params.systemId}
              />
            ),
          },
          {
            align: 'center',
            title: t('payment.channel'),
            dataIndex: 'channel',
            valueType: 'select',
            valueEnum: ChannelValueEnum,
            onFilter: true,
            width: 70,
          },
          {
            align: 'center',
            title: t('payment.token'),
            dataIndex: 'token',
            valueType: 'select',
            valueEnum: Token,
            onFilter: true,
            width: 70,
          },
          {
            // 同用戶錢包頁 —— 整串顯示 + 一鍵複製,不再截成 ellipsis。
            hideInSearch: false,
            width: 300,
            align: 'center',
            title: '錢包地址',
            dataIndex: 'walletAddress',
            render: (n, row) => (
              <Typography.Text
                copyable={{ text: row.walletAddress }}
                style={{ fontSize: 12, whiteSpace: 'nowrap' }}
              >
                {row.walletAddress}
              </Typography.Text>
            ),
          },
          {
            hideInSearch: true,
            width: 80,
            align: 'right',
            title: '餘額',
            dataIndex: 'balance',
          },
          {
            hideInSearch: true,
            width: 100,
            align: 'center',
            title: '更新時間',
            dataIndex: 'balanceUpdatedAt',
            render: (text: any, row: any) =>
              transfer2LocalTime(new Date(row.balanceUpdatedAt)),
          },
          {
            hideInSearch: true,
            width: 80,
            align: 'right',
            title: '帳變餘額',
            dataIndex: 'txnBalance',
          },
          {
            hideInSearch: true,
            width: 80,
            align: 'right',
            title: '可用餘額',
            dataIndex: 'availableBalance',
          },
          {
            align: 'center',
            width: 80,
            title: '可用狀態',
            dataIndex: 'available',
            valueType: 'select',
            valueEnum: {
              true: { text: '可用', status: 'Success' },
              false: { text: '不可用', status: 'Default' },
            },
          },
          {
            title: '操作',
            width: 100,
            key: 'actions',
            valueType: 'option',
            fixed: 'right',
            align: 'center',
            render: (dom, row: MchWallet) => (
              <Space>
                {/*<Button*/}
                {/*  key={1}*/}
                {/*  hidden={row.token !== 'USDT'}*/}
                {/*  size={'small'}*/}
                {/*  disabled={!_module.can([SYNC_MCH_BLOCKCHAIN])}*/}
                {/*  type="primary"*/}
                {/*  onClick={() => {*/}
                {/*    setCurrentRow(row);*/}
                {/*    setRestoreFormVisible(true);*/}
                {/*  }}*/}
                {/*>*/}
                {/*  回補充值交易*/}
                {/*</Button>*/}
                {row.token === Token.USDT && (
                  <Dropdown.Button
                    key={2}
                    size={'small'}
                    overlay={() => (
                      <Menu>
                        <Menu.Item
                          disabled={
                            row.systemStatus === SystemStatusEnum.Activated ||
                            !_module.can([CHANGE_WALLET])
                          }
                          key={1}
                          onClick={(x) => {
                            replaceWallet({
                              systemId: row.systemId,
                              channel: row.channel,
                            }).catch((e) => console.error(e));
                          }}
                        >
                          更換商戶錢包
                        </Menu.Item>
                        <Menu.Item
                          key={2}
                          disabled={
                            parseFloat(row.txnBalance as any) === 0 ||
                            !_module.can([ADJUST_PAYMENT])
                          }
                          onClick={(x) =>
                            setChangeAccount({
                              systemId: row.systemId,
                              visible: true,
                              walletAddress: row.walletAddress,
                              channel: row.channel,
                              token: row.token,
                              availableBalance: parseFloat(
                                row.availableBalance,
                              ),
                              txnBalance: parseFloat(row.txnBalance),
                              walletType: 'Mch',
                            })
                          }
                        >
                          調帳
                        </Menu.Item>
                        {row.available &&
                          _module.can(
                            [WRITE_ABANDON_WALLET],
                            'ABANDON-WALLET',
                          ) && (
                            <Menu.Item
                              key={5}
                              danger
                              disabled={!isRetireSupported(row.channel)}
                              onClick={() => doDisable(row)}
                            >
                              停用
                              {!isRetireSupported(row.channel) &&
                                `(${RETIRE_UNSUPPORTED_HINT})`}
                            </Menu.Item>
                          )}
                        {!row.available &&
                          _module.can(
                            [WRITE_ABANDON_WALLET],
                            'ABANDON-WALLET',
                          ) && (
                            <Menu.Item
                              key={3}
                              danger
                              disabled={!isRetireSupported(row.channel)}
                              onClick={() => doAbandon(row)}
                            >
                              棄用
                              {!isRetireSupported(row.channel) &&
                                `(${RETIRE_UNSUPPORTED_HINT})`}
                            </Menu.Item>
                          )}
                      </Menu>
                    )}
                  >
                    <div style={{ fontSize: '12px' }}>其它</div>
                  </Dropdown.Button>
                )}
              </Space>
            ),
          },
        ]}
      />
      {/*<MchDepositTxRestoreFrom*/}
      {/*  systemId={currentRow?.systemId!}*/}
      {/*  visible={restoreFormVisible}*/}
      {/*  onClose={() => {*/}
      {/*    setRestoreFormVisible(false);*/}
      {/*  }}*/}
      {/*/>*/}
      <ChangeAccountForm {...changeAccount} />
      <profitForm.ProfitWithdrawFrom {...profit} />
    </>
  );
};
